import styled from "styled-components";

import { entryColors, entryFont } from "@entry/tokens";
import { Translations, i18n } from "@i18n";

import { AccountTypeCardContent } from "./types";

type AccountTypeCardProps = AccountTypeCardContent & {
  selected: boolean;
  onSelect: () => void;
  showExclusive?: boolean;
};

const Card = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  width: 260px;
  min-width: 260px;
  box-sizing: border-box;
  text-align: left;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
  border: 2px solid
    ${({ $selected }) => ($selected ? "rgba(205, 128, 84, 0.8)" : "rgba(255, 255, 255, 0.12)")};
  background: ${({ $selected }) => ($selected ? "rgba(205, 128, 84, 0.1)" : entryColors.glassFill)};
  box-shadow: ${({ $selected }) => ($selected ? "0 0 24px rgba(205, 128, 84, 0.25)" : "none")};
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  transition:
    background 180ms ease,
    border-color 180ms ease,
    box-shadow 180ms ease;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? "rgba(205, 128, 84, 0.14)" : entryColors.glassFillStrong};
  }

  &:focus-visible {
    outline: 2px solid ${entryColors.highlight};
    outline-offset: 2px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Title = styled.div`
  flex: 1;
  font-family: ${entryFont.title};
  font-size: 18px;
  font-weight: 600;
  color: ${entryColors.textPrimary};
`;

const Indicator = styled.span<{ $selected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid
    ${({ $selected }) => ($selected ? entryColors.terracotta : "rgba(255, 255, 255, 0.3)")};
  background: ${({ $selected }) => ($selected ? entryColors.terracotta : "transparent")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Description = styled.div`
  margin-top: 6px;
  font-family: ${entryFont.body};
  font-size: 13px;
  line-height: 1.4;
  color: ${entryColors.textSecondary};
`;

const ExclusiveBadge = styled.span`
  display: inline-flex;
  align-self: flex-start;
  padding: 3px 10px;
  margin-top: 8px;
  border-radius: 999px;
  background: rgba(61, 106, 82, 0.25);
  border: 1px solid rgba(61, 106, 82, 0.4);
  font-family: ${entryFont.body};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: #9dcbbb;
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 16px 16px;
`;

const InfoBox = styled.div`
  padding: 10px;
  border-radius: 8px;
  background: rgba(244, 241, 242, 0.05);
`;

const InfoLabel = styled.div`
  font-family: ${entryFont.body};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  margin-bottom: 3px;
`;

const StrengthLabel = styled(InfoLabel)`
  color: #9dcbbb;
`;

const LimitationLabel = styled(InfoLabel)`
  color: ${entryColors.highlight};
`;

const InfoText = styled.div`
  font-family: ${entryFont.body};
  font-size: 12px;
  line-height: 1.35;
  color: ${entryColors.textSecondary};
`;

const CheckIcon = () => (
  <svg width="10" height="8" viewBox="0 0 12 10" fill="none" aria-hidden="true">
    <path
      d="M1 5.2L4.2 8.4L11 1.6"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const AccountTypeCard = ({
  selected,
  onSelect,
  title,
  description,
  strength,
  limitation,
  showExclusive,
}: AccountTypeCardProps) => {
  return (
    <Card
      type="button"
      role="radio"
      aria-checked={selected}
      $selected={selected}
      onClick={onSelect}
    >
      <Content>
        <TitleRow>
          <Indicator $selected={selected}>{selected ? <CheckIcon /> : null}</Indicator>
          <Title>{title}</Title>
        </TitleRow>
        <Description>{description}</Description>
        {showExclusive && (
          <ExclusiveBadge>{i18n.t(Translations.AccountTypeRouteExclusiveLabel)}</ExclusiveBadge>
        )}
      </Content>

      <InfoRow>
        <InfoBox>
          <StrengthLabel>{i18n.t(Translations.AccountTypeRouteStrengthLabel)}</StrengthLabel>
          <InfoText>{strength}</InfoText>
        </InfoBox>
        <InfoBox>
          <LimitationLabel>{i18n.t(Translations.AccountTypeRouteLimitationLabel)}</LimitationLabel>
          <InfoText>{limitation}</InfoText>
        </InfoBox>
      </InfoRow>
    </Card>
  );
};
